import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Twitter, Instagram, Facebook, Youtube, Send, Loader2 } from "lucide-react";
import { subscribeNewsletter } from "@/lib/api";

const SOCIALS = [
  { icon: Twitter, label: "Twitter", href: "#" },
  { icon: Instagram, label: "Instagram", href: "#" },
  { icon: Facebook, label: "Facebook", href: "#" },
  { icon: Youtube, label: "YouTube", href: "#" },
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Please enter a valid email address");
    setLoading(true);
    try {
      const res = await subscribeNewsletter({ email: email.trim(), honeypot: "" });
      toast.success(res.message || "Subscribed!");
      setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Subscription failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const openCookiePrefs = () => window.dispatchEvent(new Event("open-cookie-preferences"));

  return (
    <footer className="relative mt-32 border-t border-white/10">
      <div className="mx-auto max-w-6xl px-6 py-16">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-5">
          {/* brand + newsletter */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-gold" strokeWidth={1.5} />
              <span className="font-display text-lg font-medium text-white">
                Cosmic<span className="text-gold-gradient">Blueprint</span>
              </span>
            </Link>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-zinc-400">
              Accurate natal charts powered by the Swiss Ephemeris. Discover the secrets written in the stars —
              free, beautiful, and detailed.
            </p>

            <form data-testid="newsletter-form" onSubmit={subscribe} className="mt-6 max-w-sm">
              <label className="text-xs uppercase tracking-widest text-zinc-500">Join our newsletter</label>
              <div className="mt-2 flex gap-2">
                <input
                  data-testid="newsletter-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none transition-colors placeholder:text-zinc-500 focus:border-gold/60"
                />
                <button data-testid="newsletter-submit" type="submit" disabled={loading}
                  className="flex shrink-0 items-center gap-1.5 rounded-full bg-gradient-to-r from-nebula to-gold px-4 py-2.5 text-sm font-medium text-white transition-transform hover:scale-105 disabled:opacity-70">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
            </form>

            <div className="mt-6 flex gap-3">
              {SOCIALS.map((s) => (
                <a key={s.label} href={s.href} aria-label={s.label} data-testid={`footer-social-${s.label.toLowerCase()}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-zinc-300 transition-colors hover:border-gold/40 hover:text-gold">
                  <s.icon className="h-4.5 w-4.5" strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-display text-sm font-medium text-white">Explore</h4>
            <ul className="mt-4 space-y-3 text-sm text-zinc-400">
              <li><Link to="/generate" className="transition-colors hover:text-gold">Birth Chart Calculator</Link></li>
              <li><Link to="/compatibility" className="transition-colors hover:text-gold">Compatibility</Link></li>
              <li><Link to="/premium" className="transition-colors hover:text-gold">Premium Report</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-sm font-medium text-white">Company</h4>
            <ul className="mt-4 space-y-3 text-sm text-zinc-400">
              <li><Link to="/about" className="transition-colors hover:text-gold">About Us</Link></li>
              <li><Link to="/contact" className="transition-colors hover:text-gold">Contact</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-sm font-medium text-white">Legal</h4>
            <ul className="mt-4 space-y-3 text-sm text-zinc-400">
              <li><Link to="/privacy" data-testid="footer-privacy" className="transition-colors hover:text-gold">Privacy Policy</Link></li>
              <li><Link to="/terms" data-testid="footer-terms" className="transition-colors hover:text-gold">Terms &amp; Conditions</Link></li>
              <li><Link to="/cookies" data-testid="footer-cookies" className="transition-colors hover:text-gold">Cookie Policy</Link></li>
              <li><button onClick={openCookiePrefs} data-testid="footer-cookie-prefs" className="transition-colors hover:text-gold">Cookie Preferences</button></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/10 pt-8 sm:flex-row">
          <p className="text-xs text-zinc-500">© {new Date().getFullYear()} Cosmic Blueprint. All rights reserved.</p>
          <p className="font-serif text-sm italic text-zinc-400">"As above, so below."</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
